import { Alert, Loader, Stack, Typography } from "@octopus-energy/coral/atoms"
import { ZodFormik } from "@oefr/ui"
import { useTranslations } from "next-intl"
import { FormActions } from "@/components/organisms"
import { SubscriptionContainer } from "@/features/subscription"
import { EligibilityLoadingModal } from "@/features/subscription/meterEligibility"
import { useExtractDataFromInvoice } from "../../hooks/useExtractDataFromInvoice"
import { useExtractPDLFromInvoice } from "../../hooks/useExtractPDLFromInvoice"
import { getInvoiceUploadSchema } from "../../schemas/invoiceUploadSchema"
import { type InvoiceUploadFormValues } from "../../types/invoiceUploadFormValues"
import { type InvoiceUploadSubmitValues } from "../../types/invoiceUploadSubmitValues"
import { fileToBase64 } from "../../utils/fileToBase64"
import { InvoiceUploadCardFormFields } from "./InvoiceUploadCardFormFields"

type InvoiceUploadCardProps = {
    givenName: string
    familyName: string
    validateInvoiceUpload: (submitValues: InvoiceUploadSubmitValues) => Promise<void>
    validateInvoiceUploadError: Error | null
}

// --- TODO : extract duplicate logic from MeterPointIdForm and reuse here ---
export function InvoiceUploadCard(props: InvoiceUploadCardProps) {
    const t = useTranslations("InvoiceUploadCard")

    const extractPDLFromInvoice = useExtractPDLFromInvoice()
    const extractDataFromInvoice = useExtractDataFromInvoice()

    const handleSubmit = async (values: InvoiceUploadFormValues) => {
        if (!values.file) {
            return
        }

        const encodedPdf = await fileToBase64(values.file)

        const [prmId, invoiceResult] = await Promise.all([
            extractPDLFromInvoice.mutateAsync(encodedPdf),
            extractDataFromInvoice.mutateAsync(encodedPdf),
        ])

        if (!prmId) {
            return
        }

        await props.validateInvoiceUpload({
            givenName: values.givenName,
            familyName: values.familyName,
            invoiceMeterPointId: prmId,
            invoiceResult,
        })
    }

    return (
        <ZodFormik
            onSubmit={handleSubmit}
            initialValues={{
                givenName: props.givenName,
                familyName: props.familyName,
                file: null,
            }}
            validationSchema={getInvoiceUploadSchema(t)}
        >
            {({ values, isSubmitting }) => (
                <SubscriptionContainer.Form>
                    <EligibilityLoadingModal isLoading={isSubmitting} />
                    {extractPDLFromInvoice.error && (
                        <Alert severity="error">
                            <b>{t("submit.analysisError.title")}</b>
                            <br />
                            {t("submit.analysisError.info")}
                        </Alert>
                    )}
                    <InvoiceUploadCardFormFields />

                    {values.file && (
                        <FormActions step>
                            <FormActions.Error
                                error={
                                    extractPDLFromInvoice.error || props.validateInvoiceUploadError
                                }
                            />
                            <FormActions.Accept
                                loadingLabel={
                                    <Stack alignItems="center">
                                        <Loader size={20} />
                                        {t("submit.loadingLabel")}
                                    </Stack>
                                }
                            >
                                {t("submit.buttonLabel")}
                            </FormActions.Accept>
                            <Typography textStyle="caption">{t("submit.consent")}</Typography>
                        </FormActions>
                    )}
                </SubscriptionContainer.Form>
            )}
        </ZodFormik>
    )
}

InvoiceUploadCard.messages = [
    "InvoiceUploadCard",
    ...InvoiceUploadCardFormFields.messages,
    ...EligibilityLoadingModal.messages,
] as const
